import React from 'react';
import InsiderThreatRiskProfiler from './components/insiderthreatprofiler';
import WelcomeModal from './components/WelcomeModal';
import './styles/index.css';

function App() {
  return (
    <div className="App">
      <WelcomeModal />
      {/* SEO-optimized header section */}
      <header className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 text-white py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">2026 Edition</span>
            <span className="text-blue-300 text-sm">Updated with AI & GenAI Controls</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 leading-tight">2026 Insider Threat Risk Assessment</h1>
          <p className="text-xl mb-8 text-blue-100">
            Evaluate your security posture across six domains: remote work, identity verification, data monitoring, behavior analytics, offboarding — and the new frontier of <strong className="text-amber-300">AI & GenAI controls</strong>.
          </p>
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm border border-white/15 hover:bg-white/15 transition-colors">
              <h2 className="text-lg font-semibold mb-2">AI & GenAI Controls</h2>
              <p className="text-blue-100 text-sm">Assess GenAI data leakage risks, shadow AI governance, agentic AI access, and nation-state fraudulent worker detection</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm border border-white/15 hover:bg-white/15 transition-colors">
              <h2 className="text-lg font-semibold mb-2">Identity & Deepfake Protection</h2>
              <p className="text-blue-100 text-sm">Evaluate defenses against AI voice cloning, deepfake hiring fraud, credential theft, and DPRK IT worker infiltration</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm border border-white/15 hover:bg-white/15 transition-colors">
              <h2 className="text-lg font-semibold mb-2">Behavioral Analytics</h2>
              <p className="text-blue-100 text-sm">Measure your capability to detect anomalous behavior, quiet quitting risk, and data exfiltration patterns before damage occurs</p>
            </div>
          </div>
          <div className="bg-amber-400/20 p-4 rounded-xl border border-amber-400/40">
            <p className="text-base text-amber-100">
              <strong className="text-amber-300">CISA & FBI Advisory:</strong> Active 2025–2026 alerts warn of nation-state fraudulent IT workers and GenAI-enabled insider threats. This assessment now covers both vectors with dedicated evaluation criteria.
            </p>
          </div>
        </div>
      </header>

      {/* Main Profiler Application */}
      <main>
        <InsiderThreatRiskProfiler />
      </main>

      {/* Site Sponsors Section */}
      <section id="site-sponsors" className="bg-gray-100 py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Site Sponsors</h2>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <a href="https://www.securitycareers.help/" target="_blank" rel="noopener noreferrer" className="sponsor-link">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center min-w-[200px]">
                <h3 className="font-semibold text-blue-700">Security Careers Help</h3>
                <p className="text-sm text-gray-600 mt-1">Career resources for security professionals</p>
              </div>
            </a>
            <a href="https://cisomarketplace.services/" target="_blank" rel="noopener noreferrer" className="sponsor-link">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center min-w-[200px]">
                <h3 className="font-semibold text-blue-700">CISO Marketplace</h3>
                <p className="text-sm text-gray-600 mt-1">Services for security executives</p>
              </div>
            </a>
            <a href="https://cyberinsurancecalc.com/" target="_blank" rel="noopener noreferrer" className="sponsor-link">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center min-w-[200px]">
                <h3 className="font-semibold text-blue-700">Cyber Insurance Calc</h3>
                <p className="text-sm text-gray-600 mt-1">Insurance estimation tools</p>
              </div>
            </a>
            <a href="https://cisobudgetbuilder.com/" target="_blank" rel="noopener noreferrer" className="sponsor-link">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center min-w-[200px]">
                <h3 className="font-semibold text-blue-700">CISO Budget Builder</h3>
                <p className="text-sm text-gray-600 mt-1">Security budget planning tools</p>
              </div>
            </a>
            <a href="https://irmaturityassessment.com/" target="_blank" rel="noopener noreferrer" className="sponsor-link">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center min-w-[200px]">
                <h3 className="font-semibold text-blue-700">IR Maturity Assessment</h3>
                <p className="text-sm text-gray-600 mt-1">Incident response evaluation</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* SEO-friendly footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Insider Threat Risk Profiler</h3>
              <p className="text-gray-300">
                A comprehensive assessment tool for security leaders to evaluate and address insider threats in modern work environments.
              </p>
              <div className="mt-4">
                <a href="https://cyberadx.network/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors flex items-center">
                  <span>Part of CyberAdx Network</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">6 Assessment Domains</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Remote work security evaluation</li>
                <li>• Identity verification &amp; deepfake defense</li>
                <li>• Data access control analysis</li>
                <li>• Employee behavior analytics</li>
                <li>• Offboarding procedure evaluation</li>
                <li>• AI &amp; GenAI controls <span className="text-amber-400 text-xs font-semibold">NEW 2026</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className="text-gray-300 space-y-2">
                <li><a href="https://www.securitycareers.help/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">Security Careers Portal</a></li>
                <li><a href="https://cisomarketplace.services/careers" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">CISO Marketplace Careers</a></li>
                <li><a href="https://podcast.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">CISO Podcast Network</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">© 2026 Security Careers. All rights reserved.</p>
            <div className="flex space-x-4">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              <a href="https://cyberciso.support/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 